import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import {
  ArrowLeft, Plus, Upload, Search, Loader2, CheckCircle2, Trash2, UserCheck,
  CalendarClock, AlertTriangle, DollarSign, Users, Trophy, Settings as SettingsIcon,
  Link2, Mail, ListTodo, KanbanSquare, Lock, Sparkles,
} from 'lucide-react';

const SOURCE_COLORS = {
  manual: 'bg-slate-500', website: 'bg-emerald-600', marketplace: 'bg-indigo-600',
  import: 'bg-amber-600', referral: 'bg-pink-600',
};
const money = (cents) => cents == null ? '—' : `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fullName = (l) => [l.first_name, l.last_name].filter(Boolean).join(' ') || l.company_name || l.email || 'Unnamed lead';

const EMPTY_LEAD = {
  first_name: '', last_name: '', company_name: '', email: '', phone: '',
  event_type: '', event_date: '', guest_count: '', budget_cents: '', city: '', state: '', notes: '',
};

export default function AgencyCrm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === 'agency';
  const [tab, setTab] = useState('pipeline');
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [members, setMembers] = useState([]);
  const [mkt, setMkt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l, d, mem, ms] = await Promise.all([
        api.get('/agency/crm/stages'),
        api.get('/agency/crm/leads'),
        api.get('/agency/crm/dashboard'),
        api.get('/agency/crm/members').catch(() => ({ data: [] })),
        api.get('/agency/crm/marketplace').catch(() => ({ data: null })),
      ]);
      setStages(s.data || []);
      setLeads(l.data || []);
      setDash(d.data);
      setMembers(mem.data || []);
      setMkt(ms.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load CRM');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshLeads = useCallback(async () => {
    const [l, d] = await Promise.all([api.get('/agency/crm/leads'), api.get('/agency/crm/dashboard')]);
    setLeads(l.data || []); setDash(d.data);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(l => `${l.first_name || ''} ${l.last_name || ''} ${l.company_name || ''} ${l.email || ''} ${l.phone || ''}`.toLowerCase().includes(q));
  }, [leads, query]);

  const clientLeads = filtered.filter(l => l.is_client);

  const moveLead = async (lead, stageId) => {
    try {
      await api.post(`/agency/crm/leads/${lead.id}/move`, { stage_id: stageId });
      await refreshLeads();
    } catch (e) { toast.error('Move failed'); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-crm-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-3">
          {isOwner && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/agency/dashboard')} data-testid="crm-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate('/agency/team')} data-testid="crm-team-link">
            <Users className="w-4 h-4 mr-1" /> Team
          </Button>
        </div>
        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <KanbanSquare className="w-6 h-6 text-cyan-600" /> Leads & CRM
        </h1>
        <p className="text-sm text-slate-500 mb-4">Your private pipeline — only your agency can see these leads, notes and clients.</p>

        {dash && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            <Stat testid="crm-stat-open" icon={Users} label="Open leads" value={dash.totals.open} />
            <Stat testid="crm-stat-won" icon={Trophy} label="Won" value={dash.totals.won} color="text-green-600" />
            <Stat testid="crm-stat-clients" icon={UserCheck} label="Clients" value={dash.totals.clients} />
            <Stat testid="crm-stat-pipeline" icon={DollarSign} label="Pipeline value" value={money(dash.totals.pipeline_value_cents)} />
            <Stat testid="crm-stat-overdue" icon={AlertTriangle} label="Tasks overdue" value={dash.tasks.overdue} color={dash.tasks.overdue ? 'text-red-600' : ''} />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 max-w-full overflow-x-auto flex">
            <TabsTrigger value="pipeline" data-testid="crm-tab-pipeline"><KanbanSquare className="w-4 h-4 mr-1" />Pipeline</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="crm-tab-tasks"><ListTodo className="w-4 h-4 mr-1" />Tasks</TabsTrigger>
            <TabsTrigger value="clients" data-testid="crm-tab-clients"><UserCheck className="w-4 h-4 mr-1" />Clients</TabsTrigger>
            <TabsTrigger value="settings" data-testid="crm-tab-settings"><SettingsIcon className="w-4 h-4 mr-1" />Settings</TabsTrigger>
          </TabsList>

          {/* PIPELINE */}
          <TabsContent value="pipeline">
            {mkt && mkt.locked_count > 0 && !mkt.has_access && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="crm-locked-banner">
                <div className="flex items-center gap-2 text-sm text-amber-800">
                  <Lock className="w-4 h-4 shrink-0" />
                  <span><b>{mkt.locked_count}</b> marketplace {mkt.locked_count === 1 ? 'lead is' : 'leads are'} locked. Upgrade your plan to reveal contact details and work them.</span>
                </div>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 shrink-0" onClick={() => navigate('/agency/dashboard')} data-testid="crm-upgrade-btn">
                  <Sparkles className="w-4 h-4 mr-1" /> Upgrade
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search leads…" className="pl-9" data-testid="crm-search" />
              </div>
              <Button onClick={() => setAddOpen(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="crm-add-lead"><Plus className="w-4 h-4 mr-1" />Add lead</Button>
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="crm-import"><Upload className="w-4 h-4 mr-1" />Import CSV</Button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-4" data-testid="crm-board">
              {stages.map(st => {
                const col = filtered.filter(l => l.stage_id === st.id);
                return (
                  <div key={st.id} className="w-72 shrink-0" data-testid={`crm-col-${st.id}`}>
                    <div className="flex items-center justify-between px-1 mb-2">
                      <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.color || '#64748b' }} />{st.name}
                      </span>
                      <Badge variant="secondary">{col.length}</Badge>
                    </div>
                    <div className="space-y-2 min-h-[40px]">
                      {col.map(l => (
                        <Card key={l.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setDetailId(l.id)} data-testid={`crm-card-${l.id}`}>
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium text-sm text-slate-800 truncate flex items-center gap-1">
                                {l.is_locked && <Lock className="w-3 h-3 text-amber-600 shrink-0" />}
                                {l.is_locked ? 'Locked lead' : fullName(l)}
                              </div>
                              <span className={`text-[10px] text-white px-1.5 py-0.5 rounded ${SOURCE_COLORS[l.source] || 'bg-slate-500'}`}>{l.source}</span>
                            </div>
                            {!l.is_locked && l.company_name && <div className="text-xs text-slate-500 truncate">{l.company_name}</div>}
                            {l.is_locked && <div className="text-xs text-amber-600 truncate">Upgrade to unlock contact</div>}
                            <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                              <span>{[l.event_type, l.city].filter(Boolean).join(' · ') || '—'}</span>
                              <span className="font-medium text-slate-700">{money(l.budget_cents)}</span>
                            </div>
                            <div onClick={e => e.stopPropagation()} className="mt-2">
                              <Select value={l.stage_id} onValueChange={(v) => moveLead(l, v)}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`crm-move-${l.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {stages.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* TASKS */}
          <TabsContent value="tasks"><TasksPanel onChanged={refreshLeads} /></TabsContent>

          {/* CLIENTS */}
          <TabsContent value="clients">
            {clientLeads.length === 0 ? <p className="text-sm text-slate-400">No clients yet. Convert a won lead to create one.</p> : (
              <div className="space-y-2" data-testid="crm-clients-list">
                {clientLeads.map(l => (
                  <Card key={l.id} className="cursor-pointer hover:shadow-sm" onClick={() => setDetailId(l.id)} data-testid={`crm-client-${l.id}`}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm text-slate-800">{fullName(l)}</div>
                        <div className="text-xs text-slate-500">{l.company_name || l.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">{money(l.budget_cents)}</span>
                        <Badge variant="outline" className="text-xs">{l.link_status === 'none' ? 'Not linked' : l.link_status}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings"><SettingsPanel stages={stages} onStagesChanged={load} /></TabsContent>
        </Tabs>
      </div>

      <AddLeadDialog open={addOpen} onOpenChange={setAddOpen} stages={stages} members={members} onSaved={refreshLeads} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={refreshLeads} />
      {detailId && <LeadDetail leadId={detailId} stages={stages} members={members} onClose={() => setDetailId(null)} onChanged={refreshLeads} />}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div className="rounded-lg border bg-white p-3" data-testid={testid}>
    <div className={`text-xl font-semibold ${color || 'text-slate-800'}`}>{value}</div>
    <div className="text-xs text-slate-500 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
  </div>
);

function AddLeadDialog({ open, onOpenChange, stages, members, onSaved }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const [assignee, setAssignee] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? parseInt(form.guest_count, 10) : null,
        budget_cents: form.budget_cents ? Math.round(parseFloat(form.budget_cents) * 100) : null,
        stage_id: stages[0]?.id,
        assigned_to: assignee || null,
      };
      const { data } = await api.post('/agency/crm/leads', payload);
      if (data.duplicate_warning) toast.warning('A similar lead already exists — added anyway');
      else toast.success('Lead added');
      setForm(EMPTY_LEAD); setAssignee('');
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add lead'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="crm-add-dialog">
        <DialogHeader><DialogTitle>Add lead</DialogTitle><DialogDescription>Create a new lead in your pipeline.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="First name" value={form.first_name} onChange={set('first_name')} data-testid="crm-field-first" />
          <Input placeholder="Last name" value={form.last_name} onChange={set('last_name')} data-testid="crm-field-last" />
          <Input placeholder="Company" value={form.company_name} onChange={set('company_name')} className="col-span-2" data-testid="crm-field-company" />
          <Input placeholder="Email" value={form.email} onChange={set('email')} data-testid="crm-field-email" />
          <Input placeholder="Phone" value={form.phone} onChange={set('phone')} data-testid="crm-field-phone" />
          <Input placeholder="Event type" value={form.event_type} onChange={set('event_type')} data-testid="crm-field-eventtype" />
          <Input type="date" placeholder="Event date" value={form.event_date} onChange={set('event_date')} data-testid="crm-field-eventdate" />
          <Input placeholder="Guest count" value={form.guest_count} onChange={set('guest_count')} data-testid="crm-field-guests" />
          <Input placeholder="Budget ($)" value={form.budget_cents} onChange={set('budget_cents')} data-testid="crm-field-budget" />
          <Textarea placeholder="Notes" value={form.notes} onChange={set('notes')} className="col-span-2" data-testid="crm-field-notes" />
          {members && members.length > 1 && (
            <div className="col-span-2">
              <Select value={assignee || 'unassigned'} onValueChange={(v) => setAssignee(v === 'unassigned' ? '' : v)}>
                <SelectTrigger data-testid="crm-field-assignee"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Assign to me</SelectItem>
                  {members.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="crm-save-lead">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const text = await file.text();
      const { data } = await api.post('/agency/crm/leads/import', { csv_text: text });
      setResult(data);
      toast.success(`Imported ${data.imported}, skipped ${data.skipped_duplicates} duplicate(s)`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="crm-import-dialog">
        <DialogHeader><DialogTitle>Import leads from CSV</DialogTitle>
          <DialogDescription>Columns like name, company, email, phone, event type, budget are matched automatically. Duplicates are skipped.</DialogDescription></DialogHeader>
        <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={busy} data-testid="crm-import-file"
          className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-cyan-600 file:text-white" />
        {busy && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Importing…</div>}
        {result && (
          <div className="text-sm text-slate-600 border rounded-md p-3 bg-slate-50" data-testid="crm-import-result">
            Imported <b>{result.imported}</b> · skipped <b>{result.skipped_duplicates}</b> duplicate(s)
            {result.unmapped_headers?.length > 0 && <div className="text-xs text-amber-600 mt-1">Ignored columns: {result.unmapped_headers.join(', ')}</div>}
            {result.errors?.length > 0 && <div className="text-xs text-red-600 mt-1">{result.errors.length} error(s)</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LeadDetail({ leadId, stages, members, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const load = useCallback(async () => {
    const { data } = await api.get(`/agency/crm/leads/${leadId}`);
    setLead(data);
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const reassign = async (uid) => {
    try { await api.patch(`/agency/crm/leads/${leadId}`, { assigned_to: uid }); toast.success('Reassigned'); load(); onChanged(); }
    catch (e) { toast.error('Reassign failed'); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/agency/crm/leads/${leadId}/activities`, { body: note, type: 'note' });
      setNote(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not log note'); }
  };
  const addTask = async () => {
    if (!taskTitle.trim()) return;
    try {
      await api.post('/agency/crm/tasks', { lead_id: leadId, title: taskTitle, due_at: taskDue ? new Date(taskDue + 'T12:00:00').toISOString() : null });
      setTaskTitle(''); setTaskDue(''); load(); onChanged();
      toast.success('Task added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add task'); }
  };
  const convert = async () => {
    try {
      await api.post(`/agency/crm/leads/${leadId}/convert`);
      toast.success('Converted to client'); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Convert failed'); }
  };
  const link = async (mode) => {
    try {
      await api.post(`/agency/crm/leads/${leadId}/link`, { mode });
      toast.success(mode === 'existing' ? 'Connection requested' : 'Invite sent'); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Link failed'); }
  };
  const del = async () => {
    if (!window.confirm('Delete this lead?')) return;
    await api.delete(`/agency/crm/leads/${leadId}`);
    toast.success('Lead deleted'); onClose(); onChanged();
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="crm-lead-detail">
        {!lead ? <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></div> : (
          <>
            <SheetHeader><SheetTitle className="flex items-center gap-2">{fullName(lead)}
              {lead.is_client && <Badge className="bg-green-600">Client</Badge>}</SheetTitle></SheetHeader>
            <div className="mt-4 space-y-4 text-sm">
              <div className="space-y-1 text-slate-600">
                {lead.company_name && <div>{lead.company_name}</div>}
                {lead.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{lead.email}</div>}
                {lead.phone && <div>{lead.phone}</div>}
                <div className="flex gap-3 text-xs text-slate-500 pt-1">
                  <span>{lead.event_type || 'No event'}</span>
                  {lead.event_date && <span>{lead.event_date}</span>}
                  <span className="font-medium text-slate-700">{money(lead.budget_cents)}</span>
                </div>
                {members && members.length > 1 && (
                  <div className="pt-2">
                    <label className="text-xs text-slate-500 block mb-1">Assigned to</label>
                    <Select value={lead.assigned_to || ''} onValueChange={reassign}>
                      <SelectTrigger className="h-8 text-xs w-full" data-testid="crm-reassign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        {members.map(m => <SelectItem key={m.user_id} value={m.user_id} className="text-xs">{m.name || m.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {!lead.is_client && <Button size="sm" onClick={convert} className="bg-green-600 hover:bg-green-700" data-testid="crm-convert"><UserCheck className="w-4 h-4 mr-1" />Convert to client</Button>}
                {lead.email && lead.link_status === 'none' && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => link('existing')} data-testid="crm-link-existing"><Link2 className="w-4 h-4 mr-1" />Connect account</Button>
                    <Button size="sm" variant="outline" onClick={() => link('invite')} data-testid="crm-link-invite"><Mail className="w-4 h-4 mr-1" />Invite to iEvntür</Button>
                  </>
                )}
                {lead.link_status !== 'none' && <Badge variant="outline" className="self-center" data-testid="crm-link-status">Account: {lead.link_status}</Badge>}
                <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={del} data-testid="crm-delete-lead"><Trash2 className="w-4 h-4" /></Button>
              </div>

              {/* Tasks */}
              <div>
                <div className="font-medium text-slate-700 mb-2 flex items-center gap-1"><ListTodo className="w-4 h-4" />Follow-up tasks</div>
                <div className="space-y-1 mb-2" data-testid="crm-lead-tasks">
                  {(lead.tasks || []).filter(t => t.status === 'open').map(t => (
                    <div key={t.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                      <span>{t.title}{t.due_at && <span className="text-slate-400"> · {new Date(t.due_at).toLocaleDateString()}</span>}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="New task" value={taskTitle} onChange={e => setTaskTitle(e.target.value)} className="h-8 text-xs" data-testid="crm-task-title" />
                  <Input type="date" value={taskDue} onChange={e => setTaskDue(e.target.value)} className="h-8 text-xs w-36" data-testid="crm-task-due" />
                  <Button size="sm" className="h-8" onClick={addTask} data-testid="crm-add-task"><Plus className="w-4 h-4" /></Button>
                </div>
              </div>

              {/* Activity */}
              <div>
                <div className="font-medium text-slate-700 mb-2">Activity</div>
                <div className="flex gap-2 mb-3">
                  <Textarea placeholder="Log a note / call…" value={note} onChange={e => setNote(e.target.value)} className="text-xs min-h-[38px]" data-testid="crm-note-input" />
                  <Button size="sm" onClick={addNote} data-testid="crm-add-note">Log</Button>
                </div>
                <div className="space-y-2" data-testid="crm-activity-timeline">
                  {(lead.activities || []).map(a => (
                    <div key={a.id} className="text-xs border-l-2 border-slate-200 pl-2">
                      <div className="text-slate-700">{a.body}</div>
                      <div className="text-slate-400">{a.type} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function TasksPanel({ onChanged }) {
  const [filter, setFilter] = useState('open');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/agency/crm/tasks?filter=${filter}`); setTasks(data || []); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const complete = async (t) => { await api.patch(`/agency/crm/tasks/${t.id}`, { status: 'done' }); load(); onChanged(); };
  const reschedule = async (t, due) => { await api.patch(`/agency/crm/tasks/${t.id}`, { due_at: new Date(due + 'T12:00:00').toISOString() }); load(); onChanged(); };
  const del = async (t) => { await api.delete(`/agency/crm/tasks/${t.id}`); load(); onChanged(); };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {['open', 'overdue', 'today', 'done'].map(f => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} className={filter === f ? 'bg-cyan-600' : ''} onClick={() => setFilter(f)} data-testid={`crm-taskfilter-${f}`}>
            {f[0].toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-cyan-600" /> : tasks.length === 0 ? (
        <p className="text-sm text-slate-400">No tasks.</p>
      ) : (
        <div className="space-y-2" data-testid="crm-tasks-list">
          {tasks.map(t => {
            const overdue = t.due_at && new Date(t.due_at) < new Date() && t.status === 'open';
            return (
              <Card key={t.id} data-testid={`crm-task-${t.id}`}>
                <CardContent className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      {t.lead_label && <span>{t.lead_label}</span>}
                      {t.due_at && <span className={overdue ? 'text-red-600 flex items-center gap-1' : 'flex items-center gap-1'}><CalendarClock className="w-3 h-3" />{new Date(t.due_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {t.status === 'open' && (
                      <>
                        <Input type="date" className="h-7 text-xs w-32" onChange={e => e.target.value && reschedule(t, e.target.value)} data-testid={`crm-task-resched-${t.id}`} />
                        <Button size="sm" variant="ghost" className="text-green-600 h-7" onClick={() => complete(t)} data-testid={`crm-task-done-${t.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
                      </>
                    )}
                    {t.status === 'done' && <Badge className="bg-green-600">Done</Badge>}
                    <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => del(t)} data-testid={`crm-task-del-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ stages, onStagesChanged }) {
  const [dailySummary, setDailySummary] = useState(false);
  const [newStage, setNewStage] = useState('');
  useEffect(() => { api.get('/agency/crm/settings').then(r => setDailySummary(!!r.data.daily_summary)).catch(() => {}); }, []);
  const saveSummary = async (v) => { setDailySummary(v); await api.put('/agency/crm/settings', { daily_summary: v }); toast.success('Saved'); };
  const addStage = async () => {
    if (!newStage.trim()) return;
    await api.post('/agency/crm/stages', { name: newStage });
    setNewStage(''); onStagesChanged(); toast.success('Stage added');
  };
  const delStage = async (s) => {
    const fallback = stages.find(x => x.id !== s.id);
    try {
      await api.delete(`/agency/crm/stages/${s.id}${fallback ? `?fallback_stage_id=${fallback.id}` : ''}`);
      onStagesChanged(); toast.success('Stage removed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };
  return (
    <div className="max-w-xl space-y-6">
      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-slate-800">Daily task summary email</div>
            <div className="text-xs text-slate-500">Get one email each day listing tasks due and overdue.</div>
          </div>
          <Switch checked={dailySummary} onCheckedChange={saveSummary} data-testid="crm-daily-summary" />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="text-sm font-medium text-slate-800 mb-3">Pipeline stages</div>
          <div className="space-y-2 mb-3" data-testid="crm-stage-list">
            {stages.map(s => (
              <div key={s.id} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2">
                <span className="text-sm flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color || '#64748b' }} />{s.name}
                  {s.is_won && <Badge className="bg-green-600 text-[10px]">Won</Badge>}
                  {s.is_lost && <Badge className="bg-red-500 text-[10px]">Lost</Badge>}
                </span>
                <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => delStage(s)} data-testid={`crm-del-stage-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input placeholder="New stage name" value={newStage} onChange={e => setNewStage(e.target.value)} data-testid="crm-new-stage" />
            <Button onClick={addStage} className="bg-cyan-600 hover:bg-cyan-700" data-testid="crm-add-stage"><Plus className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
